import { useEffect, useRef } from "react";
import { trackContentImpression, type ContentProperties } from "./analytics";

const IMPRESSION_DELAY_MS = 1000;

export function useContentImpression(properties: ContentProperties) {
	const elementRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		const element = elementRef.current;
		if (!element) return;
		if (!("IntersectionObserver" in window)) {
			trackContentImpression(properties);
			return;
		}

		let timer: ReturnType<typeof setTimeout> | null = null;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5);
				if (visible && !timer) {
					timer = setTimeout(() => {
						trackContentImpression(properties);
						observer.disconnect();
					}, IMPRESSION_DELAY_MS);
				} else if (!visible && timer) {
					clearTimeout(timer);
					timer = null;
				}
			},
			{ threshold: [0.5] },
		);
		observer.observe(element);

		return () => {
			observer.disconnect();
			if (timer) clearTimeout(timer);
		};
	}, [
		properties.category,
		properties.content_id,
		properties.content_kind,
		properties.feed_tab,
		properties.position,
		properties.source,
	]);

	return elementRef;
}
